import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, LinearProgress, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { getCarrierOnboardingFile } from "../api/tenantApi";

const INK = "#0B2A4A";
const BLUE = "#1E6FE8";
const GREEN = "#12A150";
const MUTED = "#64748b";
const LINE = "#e3ebf3";

const hasValue = (value) => Array.isArray(value)
  ? value.some((item) => String(item ?? "").trim())
  : value !== null && value !== undefined && String(value).trim() !== "";

const questionLabel = (key) => String(key || "")
  .replace(/_other$/, " — Other")
  .replaceAll("_", " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const fmtDateTime = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";
};

const fmtBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AnswerValue = ({ value }) => Array.isArray(value) ? (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mt: 0.6 }}>
    {value.filter(hasValue).map((item, index) => (
      <Chip key={`${item}-${index}`} size="small" label={String(item)} sx={{ height: 23, fontSize: 11.5, bgcolor: "#eef4ff", color: INK }} />
    ))}
  </Box>
) : (
  <Typography sx={{ color: INK, fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
    {String(value)}
  </Typography>
);

export default function CarrierOnboardingFileDialog({ open, onClose, carrierInsights }) {
  const submissionId = carrierInsights?.onboarding?.id || null;
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!open || !submissionId) {
      setFile(null);
      setError("");
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    setError("");
    getCarrierOnboardingFile(submissionId)
      .then((result) => { if (!cancelled) setFile(result); })
      .catch((requestError) => { if (!cancelled) setError(requestError?.response?.data?.error || requestError?.message || "Could not load the onboarding file"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, submissionId]);

  const submission = file?.submission || carrierInsights?.onboarding || null;
  const responses = useMemo(() => {
    const answers = submission?.answers && typeof submission.answers === "object" && !Array.isArray(submission.answers)
      ? submission.answers
      : {};
    return Object.entries(answers).filter(([, value]) => hasValue(value));
  }, [submission?.answers]);
  const attachments = file?.attachments || [];
  const completion = Number(submission?.completion_percent) || 0;
  const status = String(submission?.status || "draft").replaceAll("_", " ");

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: "16px", maxHeight: "88vh" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ p: 2.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, borderBottom: `1px solid ${LINE}` }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: "#eaf1fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <DescriptionOutlinedIcon sx={{ color: BLUE }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: INK, fontSize: 18, fontWeight: 800 }}>Surety onboarding file</Typography>
              <Typography sx={{ color: MUTED, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {submission?.organization_name || carrierInsights?.company?.name || "Carrier & Surety client"}
              </Typography>
            </Box>
          </Box>
          <Button onClick={onClose} aria-label="Close onboarding file" sx={{ minWidth: 36, color: MUTED }}><CloseIcon /></Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc" }}>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25, py: 8 }}><CircularProgress size={24} /><Typography sx={{ color: MUTED, fontSize: 13 }}>Loading the latest onboarding file…</Typography></Box>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : !submission ? (
          <Alert severity="info">No onboarding file has been started for this carrier yet.</Alert>
        ) : (
          <>
            <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "13px", p: 2, bgcolor: "#fff", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
                <Box>
                  <Typography sx={{ color: INK, fontSize: 15, fontWeight: 800 }}>Latest autosaved file</Typography>
                  <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Last saved {fmtDateTime(submission.updated_at)}</Typography>
                </Box>
                <Chip size="small" label={status} sx={{ textTransform: "capitalize", fontWeight: 800, color: completion === 100 ? GREEN : BLUE, bgcolor: completion === 100 ? "#dcfce7" : "#eaf1fe" }} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.6 }}>
                <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Section {Number(submission.current_section) || 0} of 34 · {responses.length} answered field{responses.length === 1 ? "" : "s"}</Typography>
                <Typography sx={{ color: INK, fontSize: 12.5, fontWeight: 800 }}>{completion}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={completion} sx={{ height: 7, borderRadius: 4, bgcolor: "#e8eef6", "& .MuiLinearProgress-bar": { bgcolor: completion === 100 ? GREEN : BLUE } }} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mt: 1.75 }}>
                <Box><Typography sx={{ color: MUTED, fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Owner</Typography><Typography sx={{ color: INK, fontSize: 13 }}>{submission.contact_email || "—"}</Typography></Box>
                <Box><Typography sx={{ color: MUTED, fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Submitted</Typography><Typography sx={{ color: INK, fontSize: 13 }}>{submission.submitted_at ? fmtDateTime(submission.submitted_at) : "Not submitted yet"}</Typography></Box>
              </Box>
            </Box>

            <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "13px", bgcolor: "#fff", overflow: "hidden", mb: 2 }}>
              <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                <Typography sx={{ color: INK, fontSize: 15, fontWeight: 800 }}>Filled responses</Typography>
                <Chip size="small" label="Read-only admin view" sx={{ height: 22, fontSize: 10.5, color: MUTED }} />
              </Box>
              <Divider />
              {responses.length ? responses.map(([key, value], index) => (
                <Box key={key} sx={{ p: 2, borderTop: index ? `1px solid ${LINE}` : "none" }}>
                  <Typography sx={{ color: MUTED, fontSize: 11.5, fontWeight: 700, mb: 0.45 }}>{questionLabel(key)}</Typography>
                  <AnswerValue value={value} />
                </Box>
              )) : (
                <Typography sx={{ p: 2, color: MUTED, fontSize: 13 }}>The file exists, but no answers have been saved yet.</Typography>
              )}
            </Box>

            <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "13px", bgcolor: "#fff", p: 2 }}>
              <Typography sx={{ color: INK, fontSize: 15, fontWeight: 800, mb: 1 }}>Uploaded documents</Typography>
              {attachments.length ? attachments.map((attachment) => (
                <Box key={attachment.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, py: 1, borderTop: `1px solid ${LINE}` }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: INK, fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachment.file_name}</Typography>
                    <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{[attachment.attachment_kind?.replaceAll("_", " "), fmtBytes(attachment.file_size)].filter(Boolean).join(" · ")}</Typography>
                  </Box>
                  <Button href={attachment.download_url || undefined} disabled={!attachment.download_url} target="_blank" rel="noopener noreferrer" startIcon={<DownloadIcon />} size="small" sx={{ textTransform: "none", flexShrink: 0 }}>Open file</Button>
                </Box>
              )) : <Typography sx={{ color: MUTED, fontSize: 13 }}>No supporting documents have been uploaded.</Typography>}
              {attachments.length > 0 && <Typography sx={{ color: MUTED, fontSize: 11.5, mt: 1 }}>Document links are private and expire after 15 minutes. Reload the tenant page to renew them.</Typography>}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${LINE}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
